/**
 * Custom Strap Gallery — custom-strap-gallery.js
 * Shopify OS 2.0 | Vanilla JS | No dependencies
 * Matches Delugs gallery: dynamic filters, single-image modal, prev/next nav
 */

(function () {
  'use strict';

  /* ── Utilities ── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ── Filter definitions (order controls sidebar display order) ── */
  const FILTER_DEFS = [
    { key: 'style',         label: 'Style' },
    { key: 'leather',       label: 'Leather' },
    { key: 'colour',        label: 'Colour' },
    { key: 'thread_colour', label: 'Thread Colour' },
    { key: 'lugs',          label: 'Lugs' },
    { key: 'lining',        label: 'Lining' },
    { key: 'edge',          label: 'Edge' },
    { key: 'strap_end',     label: 'Strap End' },
  ];

  /* ── GalleryApp ── */
  class GalleryApp {
    constructor(section) {
      this.section   = section;
      this.sectionId = section.dataset.sectionId;

      /* Load products from script-tag variable */
      this.products        = (window.__csgProducts && window.__csgProducts[this.sectionId]) || [];
      this.filteredProducts = [...this.products];
      this.activeFilters   = {};   // { key: Set<string> }

      /* Modal state */
      this.currentIndex = 0;

      /* Touch */
      this.touchStartX = 0;
      this.touchStartY = 0;

      /* Settings */
      this.enableKeyboard = section.dataset.enableKeyboard === 'true';

      this._bindDOM();
      this._buildFilters();
      this._bindFilterEvents();
      this._bindGalleryClicks();
      this._bindModalEvents();
      this._bindKeyboard();
      this._bindMobileDrawer();
      this._lazyLoadImages();
      this._checkURLOnLoad();
      this._updateResultsCount();
    }

    /* ─────────────────────────────────────
     * DOM references
     * ───────────────────────────────────── */
    _bindDOM() {
      const s = this.section;
      this.grid              = $('.csg-grid', s);
      this.items             = $$('.csg-item', s);
      this.resultsCount      = $('.csg-results-count', s);
      this.filterGroups      = $('.csg-filter-groups', s);
      this.activeFiltersEl   = $('.csg-active-filters', s);
      this.clearAllBtn       = $('.csg-clear-all-btn', s);
      this.sidebar           = $('.csg-sidebar', s);
      this.drawerOverlay     = $('.csg-drawer-overlay', s);
      this.mobileFilterBtn   = $('.csg-mobile-filter-btn', s);
      this.sidebarClose      = $('.csg-sidebar-close', s);

      /* Modal */
      this.modalOverlay  = $('.csg-modal-overlay', s);
      this.modalClose    = $('.csg-modal__close', s);
      this.modalImage    = $('.csg-modal__main-image', s);
      this.modalHeading  = $('.csg-modal__details-heading', s);
      this.modalRef      = $('.csg-modal__ref', s);
      this.modalImgPrev  = $('.csg-modal__img-prev', s);
      this.modalImgNext  = $('.csg-modal__img-next', s);

      /* Detail items keyed by data-detail-key */
      this.detailItems = {};
      $$('[data-detail-key]', s).forEach(el => {
        this.detailItems[el.dataset.detailKey] = el;
      });
    }

    /* ─────────────────────────────────────
     * Build filter sidebar dynamically
     * ───────────────────────────────────── */
    _buildFilters() {
      if (!this.filterGroups) return;

      FILTER_DEFS.forEach(def => {
        const counts = this._collectValues(def.key);
        if (!counts.size) return;
        this.filterGroups.appendChild(this._createFilterGroup(def, counts));
      });
    }

    _collectValues(key) {
      const map = new Map();
      this.products.forEach(p => {
        const val = p.metafields && String(p.metafields[key] || '').trim();
        if (val) map.set(val, (map.get(val) || 0) + 1);
      });
      return map;
    }

    _createFilterGroup(def, valueCounts) {
      const group = document.createElement('div');
      group.className = 'csg-filter-group';
      group.dataset.filterKey = def.key;

      const sorted = [...valueCounts.entries()].sort((a, b) => a[0].localeCompare(b[0]));

      group.innerHTML = `
        <button class="csg-filter-group__header" aria-expanded="true">
          <span>${def.label}</span>
          <span class="csg-filter-group__right">
            <button class="csg-filter-group__clear" tabindex="-1" aria-label="Clear ${def.label} filter">CLEAR</button>
            <svg class="csg-filter-group__arrow" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
              <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
        <div class="csg-filter-group__body" role="group" aria-label="${def.label} filters">
          <ul class="csg-filter-list">
            ${sorted.map(([val, count]) => `
              <li class="csg-filter-item">
                <input
                  type="checkbox"
                  id="f-${this.sectionId}-${def.key}-${this._slugify(val)}"
                  data-filter-key="${def.key}"
                  data-filter-val="${this._esc(val)}"
                >
                <label class="csg-filter-label" for="f-${this.sectionId}-${def.key}-${this._slugify(val)}">${this._esc(val)}</label>
                <span class="csg-filter-count">${count}</span>
              </li>
            `).join('')}
          </ul>
        </div>`;

      /* Toggle collapse */
      const header = group.querySelector('.csg-filter-group__header');
      header.addEventListener('click', e => {
        if (e.target.closest('.csg-filter-group__clear')) return; // don't collapse when clicking clear
        const closed = group.classList.toggle('is-closed');
        header.setAttribute('aria-expanded', String(!closed));
      });

      /* Group clear button */
      const clearBtn = group.querySelector('.csg-filter-group__clear');
      clearBtn.addEventListener('click', () => this._clearGroupFilter(def.key));

      return group;
    }

    /* ─────────────────────────────────────
     * Filter Events
     * ───────────────────────────────────── */
    _bindFilterEvents() {
      this.section.addEventListener('change', e => {
        const cb = e.target.closest('input[data-filter-key]');
        if (!cb) return;
        const key = cb.dataset.filterKey;
        const val = cb.dataset.filterVal;
        if (!this.activeFilters[key]) this.activeFilters[key] = new Set();
        cb.checked ? this.activeFilters[key].add(val) : this.activeFilters[key].delete(val);
        if (!this.activeFilters[key].size) delete this.activeFilters[key];
        this._applyFilters();
      });

      this.clearAllBtn?.addEventListener('click', () => this._clearAllFilters());
    }

    _applyFilters() {
      const hasFilters = Object.keys(this.activeFilters).length > 0;

      this.filteredProducts = this.products.filter(p => {
        if (!hasFilters) return true;
        return Object.entries(this.activeFilters).every(([key, vals]) => {
          if (!vals.size) return true;
          const v = String(p.metafields && p.metafields[key] || '').trim();
          return vals.has(v);
        });
      });

      /* Show/hide grid items */
      const handles = new Set(this.filteredProducts.map(p => p.handle));
      this.items.forEach(item => {
        item.classList.toggle('is-hidden', !handles.has(item.dataset.handle));
      });

      this._updateResultsCount();
      this._updateFilterCounts();
      this._renderActiveFilters();
      this._showEmptyState();
      this._updateClearAllBtn();
    }

    _updateFilterCounts() {
      $$('input[data-filter-key]', this.section).forEach(cb => {
        const key = cb.dataset.filterKey;
        const val = cb.dataset.filterVal;
        const others = Object.entries(this.activeFilters).filter(([k]) => k !== key);
        const count = this.products.filter(p => {
          const matchOthers = others.every(([k, vals]) => {
            if (!vals.size) return true;
            return vals.has(String(p.metafields && p.metafields[k] || '').trim());
          });
          const matchThis = String(p.metafields && p.metafields[key] || '').trim() === val;
          return matchOthers && matchThis;
        }).length;
        const countEl = cb.closest('.csg-filter-item')?.querySelector('.csg-filter-count');
        if (countEl) countEl.textContent = count;
      });

      /* Show/hide group clear buttons */
      $$('.csg-filter-group', this.section).forEach(group => {
        const key = group.dataset.filterKey;
        const hasActive = this.activeFilters[key]?.size > 0;
        group.querySelector('.csg-filter-group__clear')?.classList.toggle('is-visible', hasActive);
      });
    }

    _clearGroupFilter(key) {
      delete this.activeFilters[key];
      $$(`input[data-filter-key="${key}"]`, this.section).forEach(cb => cb.checked = false);
      this._applyFilters();
    }

    _clearAllFilters() {
      this.activeFilters = {};
      $$('input[data-filter-key]', this.section).forEach(cb => cb.checked = false);
      this._applyFilters();
    }

    _renderActiveFilters() {
      if (!this.activeFiltersEl) return;
      this.activeFiltersEl.innerHTML = '';
      Object.entries(this.activeFilters).forEach(([key, vals]) => {
        vals.forEach(val => {
          const def = FILTER_DEFS.find(d => d.key === key);
          const chip = document.createElement('div');
          chip.className = 'csg-filter-chip';
          chip.innerHTML = `
            <span class="csg-filter-chip__text">filter ${def?.label?.toLowerCase() || key}: ${this._esc(val)}</span>
            <button class="csg-filter-chip__remove" aria-label="Remove filter ${this._esc(val)}">×</button>`;
          chip.querySelector('.csg-filter-chip__remove').addEventListener('click', () => {
            this.activeFilters[key]?.delete(val);
            if (!this.activeFilters[key]?.size) delete this.activeFilters[key];
            const cb = $(`input[data-filter-key="${key}"][data-filter-val="${CSS.escape(val)}"]`, this.section);
            if (cb) cb.checked = false;
            this._applyFilters();
          });
          this.activeFiltersEl.appendChild(chip);
        });
      });
    }

    _updateClearAllBtn() {
      const hasActive = Object.values(this.activeFilters).some(s => s.size > 0);
      this.clearAllBtn?.classList.toggle('is-visible', hasActive);
    }

    _updateResultsCount() {
      if (this.resultsCount) {
        this.resultsCount.innerHTML = `<strong>${this.filteredProducts.length}</strong> of ${this.products.length} straps`;
      }
    }

    _showEmptyState() {
      let el = $('.csg-empty', this.grid);
      if (this.filteredProducts.length === 0) {
        if (!el) {
          el = document.createElement('div');
          el.className = 'csg-empty';
          el.innerHTML = `<div class="csg-empty-icon">🔍</div><p>No straps match your filters.</p>`;
          this.grid.appendChild(el);
        }
        el.style.display = '';
      } else if (el) {
        el.style.display = 'none';
      }
    }

    /* ─────────────────────────────────────
     * Gallery → open modal on click
     * ───────────────────────────────────── */
    _bindGalleryClicks() {
      if (!this.grid) return;

      /* Click */
      this.grid.addEventListener('click', e => {
        const item = e.target.closest('.csg-item');
        if (!item) return;
        if (e.ctrlKey || e.metaKey) return; // Allow opening link in new tab
        e.preventDefault();
        const idx = this.filteredProducts.findIndex(p => p.handle === item.dataset.handle);
        if (idx === -1) return;
        this._openModal(idx);
      });

      /* Keyboard: Enter / Space */
      this.grid.addEventListener('keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const item = e.target.closest('.csg-item');
        if (!item) return;
        e.preventDefault();
        const idx = this.filteredProducts.findIndex(p => p.handle === item.dataset.handle);
        if (idx === -1) return;
        this._openModal(idx);
      });
    }

    /* ─────────────────────────────────────
     * Modal — open / close / populate
     * ───────────────────────────────────── */
    _openModal(index) {
      this.currentIndex = index;
      this._populateModal();
      this.modalOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this._updateURL();
      this.modalClose?.focus();
    }

    _closeModal() {
      this.modalOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
      this._clearURL();
    }

    _populateModal() {
      const p = this.filteredProducts[this.currentIndex];
      if (!p) return;

      /* Image */
      if (this.modalImage) {
        this.modalImage.classList.add('is-loading');
        this.modalImage.onload = () => this.modalImage.classList.remove('is-loading');
        this.modalImage.src = p.image?.url || '';
        this.modalImage.alt = p.image?.alt || p.title;
      }

      /* Reference */
      if (this.modalRef) {
        const ref = p.metafields?.reference_number;
        this.modalRef.textContent = ref ? `Reference:  ${ref}` : '';
        this.modalRef.style.display = ref ? '' : 'none';
      }

      /* Detail fields */
      const mf = p.metafields || {};
      Object.entries(this.detailItems).forEach(([key, el]) => {
        const val = mf[key];
        const valueEl = el.querySelector('.csg-detail-item__value');
        if (valueEl) valueEl.textContent = val || '';
        el.classList.toggle('is-empty', !val);
      });

      /* Dynamic CTA Internal Link */
      const ctaLink = $('#csg-modal-strap-detail-link', this.section);
      if (ctaLink) {
        if (p.url) {
          ctaLink.href = p.url;
          ctaLink.textContent = `View Details for ${p.title || 'This Strap'} →`;
        } else {
          ctaLink.href = '/pages/strap-finder-v2';
          ctaLink.textContent = 'Find Compatible Watch Straps →';
        }
      }

      /* Prev/Next button state */
      const total = this.filteredProducts.length;
      if (this.modalImgPrev) this.modalImgPrev.disabled = total <= 1;
      if (this.modalImgNext) this.modalImgNext.disabled = total <= 1;
    }

    _prevProduct() {
      const total = this.filteredProducts.length;
      this.currentIndex = (this.currentIndex - 1 + total) % total;
      this._populateModal();
      this._updateURL();
    }

    _nextProduct() {
      const total = this.filteredProducts.length;
      this.currentIndex = (this.currentIndex + 1) % total;
      this._populateModal();
      this._updateURL();
    }

    /* ─────────────────────────────────────
     * Modal events
     * ───────────────────────────────────── */
    _bindModalEvents() {
      /* Close */
      this.modalClose?.addEventListener('click', () => this._closeModal());
      this.modalOverlay?.addEventListener('click', e => {
        if (e.target === this.modalOverlay) this._closeModal();
      });

      /* Prev/Next (arrows over image) */
      this.modalImgPrev?.addEventListener('click', () => this._prevProduct());
      this.modalImgNext?.addEventListener('click', () => this._nextProduct());

      /* Touch swipe on image panel */
      const imgPanel = $('.csg-modal__image-panel', this.section);
      if (imgPanel) {
        imgPanel.addEventListener('touchstart', e => {
          this.touchStartX = e.touches[0].clientX;
          this.touchStartY = e.touches[0].clientY;
        }, { passive: true });

        imgPanel.addEventListener('touchend', e => {
          const dx = e.changedTouches[0].clientX - this.touchStartX;
          const dy = e.changedTouches[0].clientY - this.touchStartY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
            dx < 0 ? this._nextProduct() : this._prevProduct();
          }
        }, { passive: true });
      }
    }

    /* ─────────────────────────────────────
     * Keyboard navigation
     * ───────────────────────────────────── */
    _bindKeyboard() {
      if (!this.enableKeyboard) return;
      document.addEventListener('keydown', e => {
        if (!this.modalOverlay?.classList.contains('is-open')) return;
        switch (e.key) {
          case 'Escape':     this._closeModal(); break;
          case 'ArrowLeft':  e.preventDefault(); this._prevProduct(); break;
          case 'ArrowRight': e.preventDefault(); this._nextProduct(); break;
        }
      });
    }

    /* ─────────────────────────────────────
     * Mobile drawer
     * ───────────────────────────────────── */
    _bindMobileDrawer() {
      const open = () => {
        this.sidebar?.classList.add('is-open');
        this.drawerOverlay?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.mobileFilterBtn?.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        this.sidebar?.classList.remove('is-open');
        this.drawerOverlay?.classList.remove('is-open');
        document.body.style.overflow = '';
        this.mobileFilterBtn?.setAttribute('aria-expanded', 'false');
      };

      this.mobileFilterBtn?.addEventListener('click', open);
      this.sidebarClose?.addEventListener('click', close);
      this.drawerOverlay?.addEventListener('click', close);
    }

    /* ─────────────────────────────────────
     * Lazy load images (IntersectionObserver)
     * ───────────────────────────────────── */
    _lazyLoadImages() {
      const imgs = $$('.csg-item__img[data-src]', this.section);
      if (!imgs.length) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const img = entry.target;
            img.src = img.dataset.src;
            delete img.dataset.src;
            observer.unobserve(img);
          });
        }, { rootMargin: '300px' });
        imgs.forEach(img => observer.observe(img));
      } else {
        imgs.forEach(img => { img.src = img.dataset.src; });
      }
    }

    /* ─────────────────────────────────────
     * URL state — store/restore product handle
     * ───────────────────────────────────── */
    _updateURL() {
      const p = this.filteredProducts[this.currentIndex];
      if (!p) return;
      const url = new URL(window.location);
      url.searchParams.set('strap', p.handle);
      history.pushState({ strap: p.handle }, '', url.toString());
    }

    _clearURL() {
      const url = new URL(window.location);
      url.searchParams.delete('strap');
      history.replaceState({}, '', url.toString());
    }

    _checkURLOnLoad() {
      const handle = new URLSearchParams(window.location.search).get('strap');
      if (!handle) return;
      const idx = this.filteredProducts.findIndex(p => p.handle === handle);
      if (idx !== -1) requestAnimationFrame(() => this._openModal(idx));
    }

    /* ─────────────────────────────────────
     * Helpers
     * ───────────────────────────────────── */
    _slugify(str) {
      return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    _esc(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  /* ── Boot ── */
  function boot() {
    $$('.csg-section').forEach(section => {
      if (section._csgInit) return;
      section._csgInit = true;
      new GalleryApp(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify Theme Editor re-init */
  document.addEventListener('shopify:section:load', e => {
    const section = e.target.querySelector('.csg-section');
    if (section) { section._csgInit = false; boot(); }
  });

})();
